import * as React from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

export type FilterBottomSheetProps = {
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  /** id do listbox interno (acessibilidade). */
  listboxId?: string;
};

/**
 * Bottom sheet compartilhado para filtros no mobile.
 * Desktop não usa este componente — filtros continuam com dropdown.
 */
export function FilterBottomSheet({
  open,
  title,
  onClose,
  children,
  listboxId,
}: FilterBottomSheetProps) {
  const [mounted, setMounted] = React.useState(false);
  const [visible, setVisible] = React.useState(false);
  const panelRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (open) {
      setMounted(true);
      const id = window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => setVisible(true));
      });
      return () => window.cancelAnimationFrame(id);
    }
    setVisible(false);
    const t = window.setTimeout(() => setMounted(false), 220);
    return () => window.clearTimeout(t);
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!mounted || typeof document === "undefined") return null;

  return createPortal(
    <div className="fixed inset-0 z-[300] md:hidden" role="presentation">
      <button
        type="button"
        aria-label="Fechar filtro"
        className={[
          "absolute inset-0 border-0 bg-black/40 transition-opacity duration-200",
          visible ? "opacity-100" : "opacity-0",
        ].join(" ")}
        onClick={onClose}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={[
          "absolute inset-x-0 bottom-0 flex max-h-[75vh] flex-col rounded-t-2xl bg-white shadow-[0_-8px_32px_rgba(0,0,0,0.18)] transition-transform duration-200 ease-out",
          visible ? "translate-y-0" : "translate-y-full",
        ].join(" ")}
      >
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-200 px-4 py-3.5">
          <h2 className="min-w-0 flex-1 truncate text-base font-semibold text-gray-900">{title}</h2>
          <button
            type="button"
            aria-label="Fechar"
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
            onClick={onClose}
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
        <div
          id={listboxId}
          role="listbox"
          aria-label={title}
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
        >
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export type FilterBottomSheetOptionProps = {
  selected: boolean;
  disabled?: boolean;
  onSelect: () => void;
  children: React.ReactNode;
  /** Conteúdo à esquerda (ex.: checkbox de categoria). */
  leading?: React.ReactNode;
};

export function FilterBottomSheetOption({
  selected,
  disabled,
  onSelect,
  children,
  leading,
}: FilterBottomSheetOptionProps) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      disabled={disabled}
      className={[
        "mb-1 flex w-full items-center gap-2 rounded-lg px-3 py-3.5 text-left text-sm transition-colors",
        selected ? "bg-client-secondary font-medium text-gray-900" : "text-gray-900 active:bg-gray-50",
        disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={onSelect}
    >
      {leading}
      <span className="min-w-0 flex-1">{children}</span>
    </button>
  );
}
